import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService, IAuthStatusSearchFilters } from 'app/um/services/auth-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { EventAggregator } from 'aurelia-event-aggregator';
import { AuthStatusReasonReloadEvent, AuthStatusReloadEvent } from 'app/um/lib/events';
import { localSort } from 'app/shared/lib/sorting';
import { I18N } from 'aurelia-i18n';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { SaveStatus } from 'app/um/components/auth-statuses/save-status';
import { AuthContext } from 'app/um/lib/auth-context';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import * as UM from 'app/um/models/essette-api';

export enum FlagOptions {
    allowEditsInProviderPortal = 'Allow Edits in Provider Portal',
    allowNullDateOfService = 'Allow Null Date of Service',
    allowStatusDateEdit = 'Allow Status Date Edit',
    isApproval = 'Is Approval',
    isOpen = 'Is Open',
    isTurnAroundTimeBase = 'Is TAT Base',
    isVoided = 'Is Voided',
    sendToClaimsSystem = 'Send to Claims System'
}

enum StatusEvents {
    dataEntryComplete = 'Data Entry Completed',
    isInitial = 'Initial Status',
    providerChangesRequested = 'Changes Requested by Provider'
}

export enum DeterminationOptionsLong {
    Approved = 'Yes, approved',
    Denied = 'Yes, denied',
    Modified = 'Yes, modified',
    Other = 'Yes, other'
}

interface CustomAuthStatus extends UM.AuthStatus {
    // Values For Grid Column Sorting
    flagValue?: string | null;
    transitionValue?: string | null;
    eventValue?: string | null;
    // Display For Grid Column Display
    flagDisplay?: string | null;
    transitionDisplay?: string | null;
    eventDisplay?: string | null;
    // Tooltip list For Grid Column
    flagToolTipDesc?: string | null;
    transitionToolTipDesc?: string | null;
    statusEventToolTipDesc?: string | null;
}

@autoinject()
export class Statuses extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected authStatuses: CustomAuthStatus[] = [];
    protected editedAuthStatus: UM.AuthStatus | null = null;

    protected allStatuses: UM.AuthStatus[] = [];

    public searchFilters: IAuthStatusSearchFilters = {
        status: '',
        transitions: []
    };

    private apiSorting: Sorting = new Sorting();
    private defaultApiSorting: Sorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    private localSortProperties: string[] = ['flagValue', 'transitionValue', 'eventValue'];
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;
    protected syncToClaimsSystemEnabled: boolean = false;

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    // Save Status Modal
    protected $saveAuthStatusModal!: IPopup;
    protected $saveAuthStatusForm!: IForm;
    protected $saveAuthStatusFormElement!: Element;
    protected $saveAuthStatus!: SaveStatus;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        private readonly context: AuthContext,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);

        this.defaultApiSorting.add({ reference: 'description', direction: SortDirection.Ascending });
        this.defaultApiSorting.add({ reference: 'flag.providerChangesRequested', direction: SortDirection.Descending });
        this.defaultApiSorting.add({ reference: 'flag.dataEntryComplete', direction: SortDirection.Descending });
        this.defaultApiSorting.add({ reference: 'flag.isInitial', direction: SortDirection.Descending });
    }

    protected bind(): void {
        this.reload(true);
    }

    protected async attached(): Promise<void> {
        // check if Sync to Claims System configured for tenant for total flag count
        await this.context.loadBasicSettings();
        this.syncToClaimsSystemEnabled = !!getApiSetting(this.context.apiSettings, 'syncToClaimsSystem.$isEnabled');
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { localSorting, apiSorting } = this;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const authStatuses: CustomAuthStatus[] = <CustomAuthStatus[]>this.authStatuses;
            if(direction === null) {
                localSorting.remove(property);
                // if no local sorts, revert to API sorts by reloading
                if(localSorting.entries.length === 0) await this.reload();
            } else {
                localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            localSort(authStatuses, localSorting);
        }
        // Else If API Sort
        else {
            if(direction === null) {
                apiSorting.remove(property);
            } else {
                apiSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            await this.reload();
        }
    }

    ////****************** Functions ************************************************////
    public async reload(refreshAllStatuses: boolean = false): Promise<void> {
        await this.loadAuthStatuses(refreshAllStatuses);
    }

    protected saveAuthStatus(authStatus?: UM.AuthStatus): void {
        this.editedAuthStatus = authStatus ?? null;
        this.$saveAuthStatusModal.open();
    }

    protected saveAuthStatusClose(): void {
        if(this.$saveAuthStatusForm.reset) this.$saveAuthStatusForm.reset();
        this.$saveAuthStatusModal.close();
    }

    // Set Custom Display
    protected setAuthStatusDetails = (authStatuses: CustomAuthStatus[], allActiveCount: number = 0): CustomAuthStatus[] => {
        authStatuses.forEach(status => {
            // transform flag data
            const flags = Object.entries({ ...status.flag })    // convert object into array of key-value pairs
                .filter(f => f[1] && f[0] in FlagOptions)       // filter out inapplicable flags and event flags
                .map(f => {                                     // regex to convert camelCased flag keys to Regular Case
                    return f[0].replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\b\w/g, c => c.toUpperCase());
                })
                .sort((a, b) => a.localeCompare(b));            // sort alphabetically
            if(flags.length) {
                const flagCount = flags.length;
                let totalFlagCount = Object.keys(FlagOptions).length;
                if(!this.syncToClaimsSystemEnabled) totalFlagCount--; // minus one total flag if Sync to Claims System not enabled
                if(flagCount === totalFlagCount) { // if all possible flags are set
                    status.flagValue = status.flagDisplay = this.i18n.tr('ui:labels.all', { what: this.i18n.tr('um:auth.status.flag.system.$long', { count: flagCount }) });
                } else if(flagCount === 1) {
                    status.flagValue = status.flagDisplay = flags[0];
                } else {
                    status.flagDisplay = status.flagValue = flagCount.toString() + ' ' + this.i18n.tr('um:auth.status.flag.system.$long', { count: flagCount });
                    let listDisplay = '<ul>';
                    flags.forEach(f => {
                        listDisplay += '<li>' + f + '</li>';
                    });
                    listDisplay += '</ul>';

                    status.flagToolTipDesc = listDisplay;
                }
            } else {
                status.flagDisplay = status.flagValue = "None";
            }

            // transform transition data
            const transitions = status.transitions || [];
            if(transitions.length) {
                const transitionCount = transitions.length;
                if(transitionCount === allActiveCount) { // if all statuses are transitions
                    status.transitionValue = status.transitionDisplay = this.i18n.tr('ui:labels.all', { what: this.i18n.tr('um:auth.status.transition.$long', { count: transitionCount }) });
                } else if(transitionCount === 1) {
                    status.transitionValue = status.transitionDisplay = transitions[0].description;
                } else {
                    status.transitionDisplay = status.transitionValue = transitionCount.toString() + ' ' + this.i18n.tr('um:auth.status.transition.$', { count: transitionCount });
                    let listDisplay = '<ul>';
                    transitions.forEach(t => {
                        listDisplay += '<li>' + t.description + '</li>';
                    });
                    listDisplay += '</ul>';

                    status.transitionToolTipDesc = listDisplay;
                }
            } else {
                status.transitionDisplay = status.transitionValue = "None";
            }

            // transform event data
            const events = Object.entries({ ...status.flag })    // convert object into array of key-value pairs
                .filter(f => f[1] && f[0] in StatusEvents)       // filter out inapplicable flags and event flags
                .map(f => {                                     // regex to convert camelCased flag values to Regular Case
                    return f[0] = this.i18n.tr(`um:auth.status.flag.${f[0]}.$`).replace(/\b\w/g, c => c.toUpperCase());
                })
                .sort((a, b) => a.localeCompare(b));            // sort alphabetically
            if(events.length) {
                const eventCount = events.length;
                if(eventCount === 1) {
                    status.eventValue = status.eventDisplay = events[0];
                } else {
                    status.eventDisplay = status.eventValue = eventCount.toString() + ` ${this.i18n.tr('um:auth.status.event.$long_plural')}`;
                    let listDisplay = '<ul>';
                    events.forEach(f => {
                        listDisplay += '<li>' + f + '</li>';
                    });
                    listDisplay += '</ul>';

                    status.statusEventToolTipDesc = listDisplay;
                }
            } else {
                status.eventDisplay = status.eventValue = "";
            }
        });

        return authStatuses;
    }

    protected async saveAuthStatusFormSuccess(): Promise<void> {
        this.saveAuthStatusClose();
        await this.reload(true);
    }

    protected async clearResults(): Promise<void> {
        this.searchFilters = {
            status: '',
            transitions: []
        };
        await this.reload();
    }

    protected getIndicatorCount(status: CustomAuthStatus): number {
        let count = 0;
        if(status.flag) {
            if(status.flag.isInitial) count++;
            if(status.flag.dataEntryComplete) count++;
            if(status.flag.providerChangesRequested) count++;
            if(!status.flag.allowEditsInProviderPortal && !!status.flag.providerChangesRequested) count++;
        }
        return count;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async activateAuthStatus(authStatus: UM.AuthStatus): Promise<void> {
        // Show confirmation dialog only if deactivating
        if(authStatus.isActive) {
            const result = await confirmation.showDeactivate('um:auth.status.$long', 1);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateAuthStatus(authStatus.id!, !authStatus.isActive);
            isSuccessful = response.isSuccessful;
            this.events.publish(new AuthStatusReloadEvent());
            this.events.publish(new AuthStatusReasonReloadEvent());
            this.reload(true);
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('um:auth.status.$long', !authStatus.isActive, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadAuthStatuses(refreshAllStatuses: boolean = false): Promise<void> {
        // always sort these to the top of the grid, no matter what (order is reverse)
        this.apiSorting.add({ reference: 'flag.providerChangesRequested', direction: SortDirection.Descending });
        this.apiSorting.add({ reference: 'flag.dataEntryComplete', direction: SortDirection.Descending });
        this.apiSorting.add({ reference: 'flag.isInitial', direction: SortDirection.Descending });

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.apiSorting.entries.length === 3 ? this.defaultApiSorting : this.apiSorting,
            includeInactive: this.includeInactive
        };

        const includes = ['transitions'];

        const fields = [
            'code',
            'description',
            'determination.isDetermination',
            'determination.result',
            'isActive',
            'flag.*',
            'transitions.description'
        ];

        try {
            this.state.beginLoad();
            const [response, allResponse] = await Promise.all([
                this.service.loadAuthStatuses(options, includes, this.searchFilters, fields, true),
                refreshAllStatuses ? this.service.loadAuthStatuses({ all: true, includeInactive: false }, undefined, undefined, ['description']) : null
            ]);
            if(allResponse) this.allStatuses = allResponse.results;
            this.authStatuses = this.setAuthStatusDetails(<CustomAuthStatus[]>response.results, allResponse?.count || 0);
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
